import React, { useState, useEffect, useMemo } from 'react';
import { useIsMobile } from '../../hooks/useResponsive';
import { useLeadsData } from './hooks/useLeadsData';
import { useUrlState } from '../../hooks/useUrlState';
import useAppStore from '../../store/appStore';
import { DesktopLeads } from './desktop';
import { MobileLeads } from './mobile';

/**
 * Main Leads container component.
 * Handles UI state and delegates to Desktop/Mobile views based on screen size.
 *
 * State layers:
 *   Global (Zustand)  → leads cache, selectedLeadId (survives navigation)
 *   URL state          → activeTab (shareable, bookmarkable)
 *   Local state        → modals, drawer visibility, context menus
 */
const Leads = () => {
    const isMobile = useIsMobile();
    const {
        leads,
        loading,
        fetchError,
        lastContactMap,
        executionMap,
        handleAddLead,
        handleUpdateStatus,
        handleUpdateDetails,
        handleArchiveLead,
        handleUnarchiveLead,
        handleDeleteLead,
        handleUpdateLead,
        handleUpdateTags,
        LEAD_STATUS,
        VALID_STATUSES,
    } = useLeadsData();

    // ── URL state for active filter tab ──
    const [activeTab, setActiveTab] = useUrlState('tab', 'all');

    // ── Zustand: persisted selected lead ──
    const selectedLeadId = useAppStore((s) => s.selectedLeadId);
    const setSelectedLeadId = useAppStore((s) => s.setSelectedLead);

    // Derive the full selectedLead object from the leads array + stored id
    const selectedLead = useMemo(
        () => (selectedLeadId ? leads.find((l) => l.id === selectedLeadId) ?? null : null),
        [leads, selectedLeadId]
    );

    // ── Ephemeral local UI state ──
    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDrawer, setShowDrawer] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);

    const openLeadDrawer = (lead) => {
        setSelectedLeadId(lead.id);
        setShowDrawer(true);
    };

    const closeDrawer = () => {
        setShowDrawer(false);
        setSelectedLeadId(null);
    };

    const toggleMenu = (e, id) => {
        e.stopPropagation();
        setOpenMenu(openMenu === id ? null : id);
    };

    // If the selected lead was deleted externally, close the drawer
    useEffect(() => {
        if (selectedLeadId && leads.length > 0 && !leads.find((l) => l.id === selectedLeadId)) {
            closeDrawer();
        }
    }, [leads, selectedLeadId]); // eslint-disable-line

    // Close menu when clicking outside
    useEffect(() => {
        const handleClick = () => setOpenMenu(null);
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    const sharedProps = {
        // UI state
        activeTab,
        setActiveTab,
        showAddModal,
        setShowAddModal,
        showImportModal,
        setShowImportModal,
        selectedLead,
        showDrawer,
        openLeadDrawer,
        closeDrawer,
        openMenu,
        toggleMenu,
        // Data
        leads,
        loading,
        lastContactMap,
        executionMap,
        // CRUD handlers
        onAddLead: handleAddLead,
        onUpdateStatus: handleUpdateStatus,
        onUpdateDetails: handleUpdateDetails,
        onArchiveLead: handleArchiveLead,
        onUnarchiveLead: handleUnarchiveLead,
        onDeleteLead: handleDeleteLead,
        onUpdateLead: handleUpdateLead,
        onUpdateTags: handleUpdateTags,
        // Constants
        LEAD_STATUS,
        VALID_STATUSES,
    };

    if (isMobile) {
        return (
            <>
                {fetchError && (
                    <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">{fetchError}</div>
                )}
                <MobileLeads {...sharedProps} />
            </>
        );
    }

    return (
        <>
            {fetchError && (
                <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">{fetchError}</div>
            )}
            <DesktopLeads {...sharedProps} />
        </>
    );
};

export default Leads;
